import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  FileSearch, AlertTriangle, ChevronRight, 
  CheckCircle, AlertCircle, Circle, Plus, Building2
} from 'lucide-react';
import './DiligenceWidget.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const STATUS_CONFIG = {
  Open: { icon: Circle, color: '#ef4444', label: 'Open' },
  Partial: { icon: AlertCircle, color: '#f59e0b', label: 'Partial' },
  Closed: { icon: CheckCircle, color: '#10b981', label: 'Closed' }
};

function DiligenceWidget({ onViewFullTracker, onSelectDeal, onNavigateToDeals }) {
  const [deals, setDeals] = useState([]);
  const [selectedDealId, setSelectedDealId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDeals();
  }, []);

  // Fetch summary when selected deal changes
  useEffect(() => {
    if (selectedDealId) {
      fetchDealSummary(selectedDealId);
    }
  }, [selectedDealId]);

  const fetchDeals = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/deals`);
      const dealsData = response.data || [];
      setDeals(dealsData);
      
      if (dealsData.length > 0) {
        setSelectedDealId(dealsData[0].id);
      }
    } catch (error) {
      console.error('Error fetching deals:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDealSummary = async (dealId) => {
    try {
      const response = await axios.get(`${API_URL}/api/deals/${dealId}/diligence/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching deal summary:', error);
      setSummary(null);
    }
  };

  const handleViewFullTracker = () => {
    if (!selectedDealId) return;
    
    const selectedDeal = deals.find(d => d.id === selectedDealId);
    if (selectedDeal && onSelectDeal) {
      onSelectDeal(selectedDeal);
    }
    if (onViewFullTracker) {
      onViewFullTracker();
    }
  };

  if (loading) {
    return (
      <div className="dw-widget">
        <div className="dw-loading">
          <div className="dw-spinner"></div>
        </div>
      </div>
    );
  }

  // No deals at all - show create deal prompt
  if (deals.length === 0) {
    return (
      <div className="dw-widget">
        <div className="dw-header">
          <h3><FileSearch size={18} /> Information Status</h3>
        </div>
        <div className="dw-empty">
          <div className="dw-empty-icon"><Building2 size={32} /></div>
          <p>No deals available</p>
          <span>Create a deal to track diligence requests</span>
          {onNavigateToDeals && (
            <button className="dw-create-deal-btn" onClick={onNavigateToDeals}>
              <Plus size={14} /> Create Deal
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="dw-widget">
      <div className="dw-header">
        <h3><FileSearch size={18} /> Information Status</h3>
      </div>

      {/* Deal Selector */}
      <div className="dw-deal-selector">
        <label>Select Deal</label>
        <select 
          className="dw-deal-select"
          value={selectedDealId || ''}
          onChange={(e) => setSelectedDealId(e.target.value)}
        >
          {deals.map(deal => (
            <option key={deal.id} value={deal.id}>
              {deal.name}
            </option>
          ))}
        </select>
      </div>

      {summary ? (
        <>
          {/* Critical Alert */}
          {summary.hasCriticalGaps && (
            <div className="dw-alert">
              <AlertTriangle size={14} />
              <span>Critical gaps: {summary.criticalGaps.map(g => g.title).join(', ')}</span>
            </div>
          )}

          {/* No Documents Warning */}
          {!summary.hasDocuments && (
            <div className="dw-warning">
              <AlertTriangle size={14} />
              <span>No documents uploaded. Tracker cannot evaluate coverage.</span>
            </div>
          )}

          {/* Progress */}
          <div className="dw-progress-section">
            <div className="dw-progress-header">
              <span className="dw-progress-label">Overall Completion</span>
              <span className="dw-progress-value">{summary.stats?.completionPct || 0}%</span>
            </div>
            <div className="dw-progress-bar">
              <div 
                className="dw-progress-fill"
                style={{ width: `${summary.stats?.completionPct || 0}%` }}
              />
            </div>
          </div>

          {/* Status Counts */}
          <div className="dw-stats">
            <div className="dw-stat">
              <span className="dw-stat-value" style={{ color: '#10b981' }}>
                {summary.stats?.closed || 0}
              </span>
              <span className="dw-stat-label">Closed</span>
            </div>
            <div className="dw-stat">
              <span className="dw-stat-value" style={{ color: '#f59e0b' }}>
                {summary.stats?.partial || 0}
              </span>
              <span className="dw-stat-label">Partial</span>
            </div>
            <div className="dw-stat">
              <span className="dw-stat-value" style={{ color: '#ef4444' }}>
                {summary.stats?.open || 0}
              </span>
              <span className="dw-stat-label">Open</span>
            </div>
          </div>

          {/* Top Open Financial Items */}
          {summary.topOpenFinancial && summary.topOpenFinancial.length > 0 && (
            <div className="dw-top-items">
              <h4>Top Open Financial Items</h4>
              <div className="dw-items-list">
                {summary.topOpenFinancial.map(item => {
                  const StatusIcon = STATUS_CONFIG[item.status].icon;
                  return (
                    <div key={item.id} className="dw-item">
                      <span className="dw-item-code">{item.code}</span>
                      <span className="dw-item-title">{item.title}</span>
                      <StatusIcon 
                        size={14} 
                        color={STATUS_CONFIG[item.status].color}
                        style={{ marginLeft: 'auto' }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* View Full Button */}
          <button className="dw-view-btn" onClick={handleViewFullTracker}>
            View Tracker
            <ChevronRight size={16} />
          </button>
        </>
      ) : (
        <div className="dw-empty-state">
          <p>Select a deal above to view diligence status</p>
        </div>
      )}
    </div>
  );
}

export default DiligenceWidget;
